/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import { Modal, ModalBody, ModalContent, ModalHeader, ModalTitle, ModalTrigger } from "../ui/Modal";
import Text from "../ui/Text";
import LoadingIndicator from "../LoadingIndicator";
import Icon from "../ui/Icon";
import { useCalendarLinks } from "../../hooks/useCalendarLinks";
import { useTranslate } from "../../hooks/useTranslate";

const CalendarItems = styled.ul`
  display: grid;
  place-items: center;
  grid-template-columns: repeat(4, 1fr);
  text-align: center;
  align-items: start;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CalendarItem = styled.a.attrs({ tabIndex: 0 })` 
  display: grid;
  place-items: center;
  gap: 8px;
  padding: 16px 8px 8px 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  text-decoration: none;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.1);
    }
  }
`;

const AddToCalendarModal = ({ bookings, bookingEntities, onlineMeetingLink, trigger }) => {
  const { _t } = useTranslate();
  const { calendarLinks, isLoading, isError, fetchBookingCalendarLink } = useCalendarLinks(bookings, bookingEntities, onlineMeetingLink);

  const handleAddToCalendarModalOpen = () => {
    if (!calendarLinks.apple) {
      fetchBookingCalendarLink();
    }
  };

  return (
    <Modal onOpen={handleAddToCalendarModalOpen}>
      {trigger && (
        <ModalTrigger>
          {trigger}
        </ModalTrigger>
      )}
      <ModalContent>
        <ModalHeader>
          <ModalTitle>add_to_calendar</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {isLoading && <LoadingIndicator page />}
          {isError && (
            <Text color="error" role="alert">
              Something went wrong
            </Text>
          )}
          {calendarLinks.apple && (
            <CalendarItems role="list">
              <li>
                <CalendarItem
                  href={calendarLinks.google}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Google Calendar, ${_t("opens in a new tab")}`}
                >
                  <Icon.GoogleCalendar size={60} aria-hidden="true" />
                  <Text color="dark" size="large">
                    Google Calendar
                  </Text>
                </CalendarItem>
              </li>

              <li>
                <CalendarItem
                  href={calendarLinks.apple}
                  download="booking.ics"
                  aria-label={`Apple Calendar, ${_t("downloads a calendar file")}`}
                >
                  <Icon.AppleCalendar size={60} aria-hidden="true" />
                  <Text color="dark" size="large">
                    Apple Calendar
                  </Text>
                </CalendarItem>
              </li>
              <li>
                <CalendarItem
                  href={calendarLinks.outlook}
                  download="booking.ics"
                  aria-label={`Outlook Calendar, ${_t("downloads a calendar file")}`}
                >
                  <Icon.OutlookCalendar size={60} aria-hidden="true" />
                  <Text color="dark" size="large">
                    Outlook Calendar
                  </Text>
                </CalendarItem>
              </li>
              <li>
                <CalendarItem
                  href={calendarLinks.yahoo}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Yahoo Calendar, ${_t("opens in a new tab")}`}
                >
                  <Icon.YahooCalendar size={60} aria-hidden="true" />
                  <Text color="dark" size="large">
                    Yahoo Calendar
                  </Text>
                </CalendarItem>
              </li>
            </CalendarItems>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddToCalendarModal;
